import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import { HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import {
  MRP_FORECAST_API_LOCATION_PARAM,
  MRP_FORECAST_LOCATION_STACK_ORDER,
  MRP_LOCATIONS,
  compareMrpAssembliesAlphabetically,
  mrpAssemblyMatchesTlaPcbaFilters,
  type MrpAssembly,
  type MrpBuildPlanRow,
  type MrpLocation,
  firstOfThisMonth,
  formatMonth,
} from "@/lib/mrp";

function buildHorizon(startMonth: string, count: number): string[] {
  const months: string[] = [];
  const [yearStr, monthStr] = startMonth.split("-");
  let year = Number.parseInt(yearStr ?? "0", 10);
  let month = Number.parseInt(monthStr ?? "1", 10);
  for (let index = 0; index < count; index += 1) {
    months.push(`${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

function endMonth(startMonth: string, count: number): string {
  const months = buildHorizon(startMonth, count + 1);
  return months[months.length - 1] ?? startMonth;
}

interface CellKey {
  assemblyId: number;
  month: string;
  location: MrpLocation;
}

function cellKey(key: CellKey): string {
  return `${key.assemblyId}|${key.location}|${key.month}`;
}

function buildPlanMapFromRows(rows: MrpBuildPlanRow[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    const monthKey = String(row.plan_month).slice(0, 10);
    map.set(
      cellKey({ assemblyId: row.assembly_id, month: monthKey, location: row.location_code }),
      row.quantity,
    );
  }
  return map;
}

type BuildPlanQueryData = { rows: MrpBuildPlanRow[] };

type BuildPlanSaveVariables = {
  key: string;
  assembly_id: number;
  plan_month: string;
  location_code: MrpLocation;
  /** `null` removes the row; a positive integer upserts. */
  quantity: number | null;
};

function patchBuildPlanQueryData(
  data: BuildPlanQueryData | undefined,
  variables: BuildPlanSaveVariables,
): BuildPlanQueryData {
  const monthKey = variables.plan_month.slice(0, 10);
  const rows = [...(data?.rows ?? [])];
  const index = rows.findIndex(
    (row) =>
      Number(row.assembly_id) === variables.assembly_id &&
      row.location_code === variables.location_code &&
      String(row.plan_month).slice(0, 10) === monthKey,
  );
  if (variables.quantity === null) {
    if (index >= 0) {
      rows.splice(index, 1);
    }
    return { rows };
  }
  if (index >= 0) {
    rows[index] = { ...rows[index], quantity: variables.quantity };
    return { rows };
  }
  rows.push({
    id: 0,
    assembly_id: variables.assembly_id,
    assembly_code: "",
    assembly_display_name: "",
    location_code: variables.location_code,
    plan_month: monthKey,
    quantity: variables.quantity,
    notes: null,
    updated_at: new Date().toISOString(),
    updated_by: null,
  });
  return { rows };
}

/** Blank in the grid: no row, quantity 0, or an empty draft. */
function formatPlanCellDisplay(quantity: number | undefined): string {
  if (quantity === undefined || quantity === 0) {
    return "";
  }
  return String(quantity);
}

/** Positive integer to save, or null when the cell should be cleared (no build plan row). */
function parseBuildPlanBlurValue(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

function parsePlanCellQuantity(
  planByCell: Map<string, number>,
  drafts: Map<string, string>,
  assemblyId: number,
  month: string,
  location: MrpLocation,
): number {
  const key = cellKey({ assemblyId, month, location });
  const persistedRaw = planByCell.get(key);
  const persisted = formatPlanCellDisplay(persistedRaw);
  const draft = drafts.get(key);
  const value = draft ?? persisted;
  const parsed = parseBuildPlanBlurValue(value);
  return parsed ?? 0;
}

function sumAssemblyMonthAcrossLocations(
  planByCell: Map<string, number>,
  drafts: Map<string, string>,
  assemblyId: number,
  month: string,
  locations: MrpLocation[],
): number {
  let total = 0;
  for (const location of locations) {
    total += parsePlanCellQuantity(planByCell, drafts, assemblyId, month, location);
  }
  return total;
}

export function BuildPlan() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const mrpReadOnlyTitle = "Your role has read-only access to the MRP hub module.";
  const [visibleLocations, setVisibleLocations] = useState<Set<MrpLocation>>(
    () => new Set<MrpLocation>(["sgs", "suntronic", "kodiak"]),
  );
  const [startMonth, setStartMonth] = useState<string>(firstOfThisMonth());
  const [horizon, setHorizon] = useState<number>(12);
  const [showTlaAssemblies, setShowTlaAssemblies] = useState(true);
  const [showPcbaAssemblies, setShowPcbaAssemblies] = useState(true);

  const months = useMemo(() => buildHorizon(startMonth, horizon), [startMonth, horizon]);
  const fromMonth = months[0] ?? firstOfThisMonth();
  const toMonth = endMonth(startMonth, horizon);

  const visibleLocationStack = useMemo(
    () => MRP_FORECAST_LOCATION_STACK_ORDER.filter((code) => visibleLocations.has(code)),
    [visibleLocations],
  );

  const assembliesQuery = useQuery({
    queryKey: ["mrp", "assemblies"],
    queryFn: () => apiFetch<{ assemblies: MrpAssembly[] }>("/api/mrp/assemblies"),
  });

  const planQueryKey = useMemo(
    (): QueryKey => [
      "mrp",
      "build-plan",
      { from: fromMonth, to: toMonth, location: MRP_FORECAST_API_LOCATION_PARAM },
    ],
    [fromMonth, toMonth],
  );

  const planQuery = useQuery({
    queryKey: planQueryKey,
    queryFn: () =>
      apiFetch<BuildPlanQueryData>("/api/mrp/build-plan", {
        query: { from: fromMonth, to: toMonth, location: MRP_FORECAST_API_LOCATION_PARAM },
      }),
    enabled: visibleLocations.size > 0,
  });

  const planByCell = useMemo(
    () => buildPlanMapFromRows(planQuery.data?.rows ?? []),
    [planQuery.data],
  );

  const [drafts, setDrafts] = useState<Map<string, string>>(new Map());
  const pendingSaveKeysRef = useRef(new Set<string>());
  const invalidateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDrafts(new Map());
  }, [fromMonth, toMonth, visibleLocationStack]);

  const scheduleBuildPlanSync = useCallback(() => {
    if (invalidateTimerRef.current !== null) {
      clearTimeout(invalidateTimerRef.current);
    }
    invalidateTimerRef.current = setTimeout(() => {
      invalidateTimerRef.current = null;
      if (pendingSaveKeysRef.current.size > 0) {
        scheduleBuildPlanSync();
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ["mrp", "build-plan"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
    }, 400);
  }, [queryClient]);

  useEffect(
    () => () => {
      if (invalidateTimerRef.current !== null) {
        clearTimeout(invalidateTimerRef.current);
      }
    },
    [],
  );

  const readPersistedDisplay = useCallback(
    (key: string): string => {
      const data = queryClient.getQueryData<BuildPlanQueryData>(planQueryKey);
      const map = buildPlanMapFromRows(data?.rows ?? []);
      return formatPlanCellDisplay(map.get(key));
    },
    [planQueryKey, queryClient],
  );

  const saveCellMutation = useMutation({
    mutationFn: async (variables: BuildPlanSaveVariables) => {
      if (variables.quantity === null) {
        return apiFetch("/api/mrp/build-plan", {
          method: "DELETE",
          query: {
            assembly_id: variables.assembly_id,
            plan_month: variables.plan_month,
            location_code: variables.location_code,
          },
        });
      }
      return apiFetch("/api/mrp/build-plan", {
        method: "PUT",
        body: {
          assembly_id: variables.assembly_id,
          plan_month: variables.plan_month,
          quantity: variables.quantity,
          location_code: variables.location_code,
        },
      });
    },
    onMutate: async (variables) => {
      pendingSaveKeysRef.current.add(variables.key);
      await queryClient.cancelQueries({ queryKey: planQueryKey });
      const previous = queryClient.getQueryData<BuildPlanQueryData>(planQueryKey);
      queryClient.setQueryData<BuildPlanQueryData>(planQueryKey, (current) =>
        patchBuildPlanQueryData(current, variables),
      );
      setDrafts((current) => {
        const next = new Map(current);
        next.delete(variables.key);
        return next;
      });
      return { previous };
    },
    onError: (_error, variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(planQueryKey, context.previous);
      }
      const restoreDisplay =
        variables.quantity === null ? "" : String(variables.quantity);
      setDrafts((current) => {
        const next = new Map(current);
        next.set(variables.key, restoreDisplay);
        return next;
      });
    },
    onSettled: (_data, _error, variables) => {
      pendingSaveKeysRef.current.delete(variables.key);
      scheduleBuildPlanSync();
    },
  });

  const assembliesList = assembliesQuery.data?.assemblies;
  const allAssemblies = assembliesList ?? [];

  const filteredSortedAssemblies = useMemo(() => {
    const list = (assembliesList ?? []).filter((assembly) =>
      mrpAssemblyMatchesTlaPcbaFilters(assembly.code, showTlaAssemblies, showPcbaAssemblies),
    );
    list.sort(compareMrpAssembliesAlphabetically);
    return list;
  }, [assembliesList, showTlaAssemblies, showPcbaAssemblies]);

  const locationLabel = useMemo(
    () => new Map(MRP_LOCATIONS.map((row) => [row.code, row.label] as const)),
    [],
  );

  const toggleLocation = (code: MrpLocation): void => {
    setVisibleLocations((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Build plan</h1>
        <p className="text-sm text-slate-600">
          Editable monthly build quantities by logical assembly. Each cell saves on blur; clear a cell to remove that
          month&apos;s plan (blank, not zero). Toggle which locations appear in the grid (the same idea as the Forecast
          page). The OrderingR importer only fills Suntronic and Kodiak; SGS is for entries you add in this app.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="block">
              <span className="block text-xs font-medium text-slate-600">Start month</span>
              <Input
                type="month"
                value={startMonth.slice(0, 7)}
                onChange={(event) => setStartMonth(`${event.target.value || firstOfThisMonth().slice(0, 7)}-01`)}
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-600">Horizon (months)</span>
              <Input
                type="number"
                min={1}
                max={36}
                value={horizon}
                onChange={(event) => setHorizon(Math.min(36, Math.max(1, Number.parseInt(event.target.value, 10) || 1)))}
              />
            </label>
          </div>
          <div className="mt-3 flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Locations</span>
            <div className="flex flex-wrap gap-2">
              {MRP_LOCATIONS.map((location) => {
                const isOn = visibleLocations.has(location.code);
                return (
                  <Button
                    key={location.code}
                    size="sm"
                    variant={isOn ? "primary" : "secondary"}
                    onClick={() => toggleLocation(location.code)}
                  >
                    {location.label}
                  </Button>
                );
              })}
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Assembly code type
            </span>
            <p className="text-xs text-slate-500">
              Filter rows by whether the assembly code contains{" "}
              <code className="rounded bg-slate-100 px-1">-TLA</code> or{" "}
              <code className="rounded bg-slate-100 px-1">-PCBA</code>. Other codes always show.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={showTlaAssemblies ? "primary" : "secondary"}
                onClick={() => setShowTlaAssemblies((current) => !current)}
              >
                TLA
              </Button>
              <Button
                size="sm"
                variant={showPcbaAssemblies ? "primary" : "secondary"}
                onClick={() => setShowPcbaAssemblies((current) => !current)}
              >
                PCBA
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{filteredSortedAssemblies.length} assemblies</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="sticky left-0 z-10 w-[10rem] min-w-[10rem] max-w-[10rem] bg-slate-50 px-3 py-2 align-bottom">Assembly</th>
                <th className="min-w-[10.5rem] border-l border-slate-200 bg-slate-50 px-2 py-2">Location and SKU</th>
                {months.map((month) => (
                  <th key={month} className="px-2 py-2 text-right">
                    {formatMonth(month)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleLocationStack.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-slate-500" colSpan={2 + months.length}>
                    Toggle on at least one location to see the build plan.
                  </td>
                </tr>
              ) : null}
              {visibleLocationStack.length > 0
                ? filteredSortedAssemblies.flatMap((assembly) => {
                    const rowSpan = visibleLocationStack.length + 1;
                    const locationRows = visibleLocationStack.map((locationCode, stackIndex) => {
                      const skuRow = assembly.skus.find((entry) => entry.location_code === locationCode);
                      const placeLabel = locationLabel.get(locationCode) ?? locationCode;
                      return (
                        <tr key={`${assembly.id}-${locationCode}`} className="align-top hover:bg-slate-50/60">
                          {stackIndex === 0 ? (
                            <td
                              className="sticky left-0 z-10 w-[10rem] min-w-0 max-w-[10rem] box-border border-r border-slate-100 bg-white px-3 py-2 align-top"
                              rowSpan={rowSpan}
                            >
                              <div className="min-w-0 break-all font-mono text-xs leading-snug text-slate-900">{assembly.code}</div>
                              {assembly.display_name && assembly.display_name !== assembly.code ? (
                                <div className="mt-0.5 break-words text-xs leading-snug text-slate-500">{assembly.display_name}</div>
                              ) : null}
                            </td>
                          ) : null}
                          <td className="min-w-[10.5rem] max-w-[15.4rem] border-r border-slate-100 px-2 py-2">
                            <div className="text-xs font-medium text-slate-800">{placeLabel}</div>
                            <div className="mt-1 break-all">
                              {skuRow ? (
                                <HoverHintContent hoverHint={`Open SKU details for ${skuRow.sku}`} block>
                                  <MrpSkuDetailLink
                                    sku={skuRow.sku}
                                    className="font-mono text-[11px] text-brand-700 hover:text-brand-900 hover:underline"
                                  >
                                    {skuRow.sku}
                                  </MrpSkuDetailLink>
                                </HoverHintContent>
                              ) : (
                                <span className="font-mono text-[11px] text-rose-700">no mapping</span>
                              )}
                            </div>
                          </td>
                          {months.map((month) => {
                            const key = cellKey({ assemblyId: assembly.id, month, location: locationCode });
                            const persistedRaw = planByCell.get(key);
                            const persisted = formatPlanCellDisplay(persistedRaw);
                            const draft = drafts.get(key);
                            const value = draft ?? persisted;
                            const dirty = draft !== undefined && draft !== persisted;
                            return (
                              <td
                                key={`${assembly.id}-${locationCode}-${month}`}
                                className={cn("px-1 py-1 text-right", dirty ? "bg-amber-50" : undefined)}
                              >
                                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    value={value}
                                    onChange={(event) => {
                                      const nextDraft = event.target.value;
                                      setDrafts((current) => {
                                        const next = new Map(current);
                                        next.set(key, nextDraft);
                                        return next;
                                      });
                                    }}
                                    onBlur={(event) => {
                                      if (!skuRow || !hubWritesAllowed) return;
                                      if (pendingSaveKeysRef.current.has(key)) {
                                        return;
                                      }
                                      const parsed = parseBuildPlanBlurValue(event.target.value);
                                      const persistedNow = readPersistedDisplay(key);
                                      const clearDraft = (): void => {
                                        setDrafts((current) => {
                                          const next = new Map(current);
                                          next.delete(key);
                                          return next;
                                        });
                                      };
                                      if (parsed === null) {
                                        if (persistedNow === "") {
                                          clearDraft();
                                          return;
                                        }
                                        saveCellMutation.mutate({
                                          key,
                                          assembly_id: Number(assembly.id),
                                          plan_month: month,
                                          location_code: locationCode,
                                          quantity: null,
                                        });
                                        return;
                                      }
                                      if (String(parsed) === persistedNow) {
                                        clearDraft();
                                        return;
                                      }
                                      saveCellMutation.mutate({
                                        key,
                                        assembly_id: Number(assembly.id),
                                        plan_month: month,
                                        location_code: locationCode,
                                        quantity: parsed,
                                      });
                                    }}
                                    className="h-7 w-20 text-right"
                                    disabled={!skuRow || !hubWritesAllowed}
                                  />
                                </ReadOnlyHoverLayer>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    });
                    const sumRow = (
                      <tr
                        key={`${assembly.id}-sum`}
                        className="border-t border-slate-200 bg-slate-100/90 align-top"
                      >
                        <td className="min-w-[10.5rem] max-w-[15.4rem] border-r border-slate-200 px-2 py-1.5">
                          <div className="text-xs font-semibold text-slate-800">Sum</div>
                        </td>
                        {months.map((month) => (
                          <td
                            key={`${assembly.id}-sum-${month}`}
                            className="px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-slate-900"
                          >
                            {sumAssemblyMonthAcrossLocations(
                              planByCell,
                              drafts,
                              assembly.id,
                              month,
                              visibleLocationStack,
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                    return [...locationRows, sumRow];
                  })
                : null}
              {allAssemblies.length === 0 && visibleLocationStack.length > 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-slate-500" colSpan={2 + months.length}>
                    No assemblies yet. Run the importer or create one on the Assemblies page.
                  </td>
                </tr>
              ) : null}
              {allAssemblies.length > 0 &&
              filteredSortedAssemblies.length === 0 &&
              visibleLocationStack.length > 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-slate-500" colSpan={2 + months.length}>
                    No assemblies match the TLA / PCBA filters. Turn at least one type on.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}
